import { memo, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  useIsAllowedPermission,
  usePermissions,
} from '@mint-lib/profile-helpers';
import styled from '@mint-lib/styled';
import { getRouteApi } from '@mint/routing';
import { saveAs } from 'file-saver';

import { Box, Button, Divider, Tooltip } from '@myn/mui';

import {
  useCreateMediaPlanExelReportMutation,
  useToggleCarbonNeutralMutation,
} from '../../../api/mediaPlansApiSlice.js';
import { planningAccess } from '../../../constants/campaignBuilder.js';
import { FeaturePermissionsConstants } from '../../../constants/FeaturePermissionsConstants.js';
import { getTooltipMessage } from '../../../constants/myCampaigns.js';
import EditModal from '../../../features/EditModal/index.js';
import { useBuilderTableViewDataSelector } from '../../../hooks/useGetBuilderTableViewData.js';
import {
  setCampaignData,
  useGetCampaignData,
} from '../../../hooks/useGetCampaignData.js';
import { useIsArchivedVersion } from '../../../hooks/useIsArchivedVersion.js';
import useIsBuyingPlanLocked from '../../../hooks/useIsBuyingPlanLocked.js';
import { useSaveBuilderTableColumnOrder } from '../../../hooks/useSaveBuilderTableView.js';
import SwitchWithLabel from '../../../molecules/SwitchWithLabel.jsx';
import { Dimension } from '../../../types/campaignBuilder.js';
import CampaignCurrencySwitch from './CampaignCurrencySwitch.jsx';
import ExportButton from './ExportButton.jsx';
import PredictiveButton from './PredictiveButton.jsx';
import RowsSelect from './RowsSelect.jsx';
import useViewportWidth from './useViewportWidth.jsx';
import { compareDimensionColumns } from './utils.js';

type Props = {
  selectedPlanId: number | null;
  climatePartnerError: string | null;
  isPredictiveLoading: boolean;
  haveMediaRows: boolean;
  status: number;
  openPredictiveMode: () => void;
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const TableSettings: React.FC<Props> = memo(
  ({
    selectedPlanId,
    climatePartnerError,
    isPredictiveLoading,
    haveMediaRows,
    status,
    openPredictiveMode,
  }) => {
    const { t } = useTranslation('@myn/planning');

    const { campaignId: id = '' } = routeApi.useParams();
    const { data: campaign } = useGetCampaignData();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isArchivedVersion] = useIsArchivedVersion();

    const { data: builderTableViewId } = useBuilderTableViewDataSelector('id');
    const { data: builderTableViewColumns } =
      useBuilderTableViewDataSelector('columns');

    const { isDesktopWidth, isTabletWidth } = useViewportWidth();
    const { getPermissionField } = usePermissions();

    const isNonFinancialUser = !getPermissionField('financialDataFeature');
    const predictivePlanningAccess = getPermissionField(
      'predictivePlanningFeature',
    );
    const campaignPlanningAccess = getPermissionField(
      'campaignPlanningFeature',
    );

    const co2FullAccess = useIsAllowedPermission(
      'carbonNeutralFeature:full_control',
    );

    const isUserReadOnlyAccess = !(
      campaignPlanningAccess === FeaturePermissionsConstants.FULL_CONTROL ||
      false
    );
    const { isLocked: isBuyingPlanLocked } = useIsBuyingPlanLocked();

    const isEditColumnBtnDisabled =
      isArchivedVersion || isUserReadOnlyAccess || isBuyingPlanLocked;

    const isPredictiveBtnDisabled =
      predictivePlanningAccess !== planningAccess.TOTAL ||
      isPredictiveLoading ||
      isNonFinancialUser ||
      isArchivedVersion ||
      !campaignPlanningAccess ||
      !haveMediaRows ||
      !status ||
      isBuyingPlanLocked;

    const co2SwitchDisabled =
      !selectedPlanId ||
      isArchivedVersion ||
      isUserReadOnlyAccess ||
      !co2FullAccess ||
      !!climatePartnerError ||
      isNonFinancialUser;

    const tooltipMessages = getTooltipMessage(t);

    const co2TooltipText =
      isUserReadOnlyAccess || !co2FullAccess
        ? tooltipMessages.CO2_NO_ACCESS
        : tooltipMessages.CO2_FULL_ACCESS;

    const co2TooltipWithClimatePartnerAccess =
      climatePartnerError ?? co2TooltipText;

    const showPredictivePlanningButton =
      !isNonFinancialUser && predictivePlanningAccess;

    const tooltipTitle = useMemo(() => {
      if (isPredictiveLoading) {
        return t("We're loading the data for this feature");
      }
      if (isPredictiveBtnDisabled) {
        return t("We don't have enough data to show");
      }
      return '';
    }, [isPredictiveLoading, isPredictiveBtnDisabled, t]);

    const saveBuilderTableColumnOrder = useSaveBuilderTableColumnOrder();
    const [createMediaPlanExelReport] = useCreateMediaPlanExelReportMutation();
    const [toggleCarbonNeutralMutation] = useToggleCarbonNeutralMutation();

    const handleEditModalOpen = () => {
      setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
      setIsEditModalOpen(false);
    };

    const handleEditModalConfirmation = async (
      dimensions: Dimension[],
      onConfirm: () => void,
      onFailed: () => void,
    ) => {
      const onSuccess = () => {
        handleEditModalClose();
        onConfirm();
      };

      const isDimensionColumnsEqual = compareDimensionColumns(
        builderTableViewColumns ?? [],
        dimensions,
      );

      if (isDimensionColumnsEqual) {
        onSuccess();
        return;
      }

      // prepare data for request
      const data = {
        columns: dimensions.map((dimension) => ({
          name: dimension.value,
        })),
      };

      if (builderTableViewId) {
        try {
          await saveBuilderTableColumnOrder({
            id: builderTableViewId,
            ...data,
          });
          onSuccess();
        } catch (error) {
          onFailed();
        }
      }
    };

    const handleExportRequest = async (versionId: number) => {
      if (selectedPlanId) {
        try {
          const { blob, fileName } = await createMediaPlanExelReport({
            id: selectedPlanId,
            versionId,
          }).unwrap();

          const file = new Blob([blob], {
            type: 'application/ms-excel',
          });
          saveAs(file, fileName);
        } catch (error) {}
      }
    };

    const handleCarbonNeutralButton = async () => {
      if (!campaign) {
        return;
      }

      const payload = !campaign.isCarbonNeutralAllowed;

      try {
        await toggleCarbonNeutralMutation({
          id,
          isCarbonNeutralAllowed: payload,
        }).unwrap();

        setCampaignData(campaign.id, {
          isCarbonNeutralAllowed: payload,
        });
      } catch (error) {
        // TODO: ask for a text of the error message
      }
    };

    return (
      <>
        <SettingsWrapper>
          <ControlsLeft>
            <Button
              color="primary"
              variant="outlined"
              size="medium"
              startIcon="Edit"
              onClick={handleEditModalOpen}
              disabled={isEditColumnBtnDisabled}
            >
              {t('Edit columns')}
            </Button>
            <Tooltip title={!isNonFinancialUser && t('Export plan')}>
              <div>
                <ExportButton
                  isDesktopWidth={isDesktopWidth}
                  disabled={isNonFinancialUser}
                  handleExportRequest={handleExportRequest}
                />
              </div>
            </Tooltip>
            {showPredictivePlanningButton ? (
              <Tooltip title={tooltipTitle} placement="bottom" arrow={false}>
                <Box>
                  <PredictiveButton
                    isLoading={isPredictiveLoading}
                    isDisabled={isPredictiveBtnDisabled}
                    isLabelVisible={isDesktopWidth}
                    onClick={openPredictiveMode}
                  />
                </Box>
              </Tooltip>
            ) : null}
            {isDesktopWidth && (
              <StyledDivider orientation="vertical" flexItem />
            )}
            {!isNonFinancialUser && (
              <RowsSelect variant={isDesktopWidth ? 'default' : 'icon'} />
            )}
          </ControlsLeft>
          <ControlsRight>
            {!isNonFinancialUser && (
              <CampaignCurrencySwitch
                size={isTabletWidth ? 'small' : 'large'}
                isDisabled={isBuyingPlanLocked}
              />
            )}
            {!isNonFinancialUser && (
              <SwitchWithLabel
                checked={!!campaign?.isCarbonNeutralAllowed}
                onClick={handleCarbonNeutralButton}
                disabled={co2SwitchDisabled}
                icon={isTabletWidth ? 'Leaf' : null}
                label={isTabletWidth ? null : t('Carbon Offset')}
                tooltip={co2TooltipWithClimatePartnerAccess}
              />
            )}
          </ControlsRight>
        </SettingsWrapper>
        <EditModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onConfirm={handleEditModalConfirmation}
        />
      </>
    );
  },
);

const SettingsWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: 'wrap';
  padding: '0px';
`;

const ControlsLeft = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ControlsRight = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(3)};
`;

const StyledDivider = styled(Divider)`
  margin: ${({ theme }) => `0px ${theme.spacing(1)}`};
`;

export default TableSettings;
